// compatibility
// error process
// npm install --save --save-exact -D react-error-overlay@6.0.9

// -- ES6 Promise polyfill in order to work

// libraries
// https://reactrouter.com/docs/en/v6/getting-started/overview
// https://dev.to/amarjits/basic-guide-to-use-react-router-v6-35do
// https://www.npmjs.com/package/react-loading-icons
// https://www.npmjs.com/package/react-loading-skeleton

// -- ICONS
// https://react-icons.github.io/react-icons
// https://fontawesome.com/v5.15
// https://feathericons.com/
// https://ant.design/components/icon/

// - SENTENCE CASES
// https://medium.com/@robertsavian/javascript-case-converters-using-lodash-4f2f964091cc

import React, {
    useRef,
    useState
} from "react";

import {
    BrowserRouter,
    Routes,
    Route,
    Navigate
} from "react-router-dom";

import ScrollToTop from "utils/ScrollToTop";
import Previous from "utils/Pages/Previous";
import ProtectedRoute from "components/Router/ProtectedRoute";

import PageAuthentication from "view/Authentication/PageAuthentication";
import PageMessages from "view/PageMessages";
import PageTerms from "view/PageTerms";
import PageCookies from "view/PageCookies";

import {
    Helmet,
    HelmetProvider
} from "react-helmet-async";

import {
    useTranslation
} from "react-i18next";

import {
    isAuthOnLoad
} from "utils/Requests/Auth";

import LoadingBar from "react-top-loading-bar";

import PageDashboard from "./view/PageDashboard";

import removeFbclid from "remove-fbclid";

import "moment/min/locales";

import "antd/dist/antd.css";

import "assets/css/bootstrap.css";
import "assets/css/bootstrap-extended.css";
import "assets/css/colors.css";
import "assets/css/components.css";

import "assets/css/themes/dark-layout.min.css";
import "assets/css/themes/bordered-layout.min.css";
import "assets/css/themes/semi-dark-layout.min.css";

import "assets/css/core/menu/menu-types/horizontal-menu.min.css";

import "react-loading-skeleton/dist/skeleton.css";

import "app.scss";


// COMPONENT TO SET
// https://pixinvent.com/demo/vuexy-react-admin-dashboard-template/demo-2/components/badges
// https://pixinvent.com/demo/vuexy-react-admin-dashboard-template/demo-2/components/breadcrumbs
// https://pixinvent.com/demo/vuexy-react-admin-dashboard-template/demo-2/extensions/pagination
// https://pixinvent.com/demo/vuexy-react-admin-dashboard-template/demo-2/extensions/export
// https://pixinvent.com/demo/vuexy-react-admin-dashboard-template/demo-2/extensions/export-selected

// TODO HEADER SE BE AL SCROLL
// TODO REVISAR RESPONSIVE DE LAS CARDS DEL MAIN


/*
 * ============================================================
 * REMOVE FBCLID
 * ============================================================
 */

removeFbclid();


/*
 * ============================================================
 * GLOBAL CONFIG
 * ============================================================
 */

// set progress bar color
window.progressBarColor = "#b9b9b9";

// general interval time
window.analysisTimerRefresh = 1800000;


/*
 * ============================================================
 * MAIN APP
 * ============================================================
 */

export default function App() {

    const {
        t
    } = useTranslation();


    /*
     * Save progress bar reference on window
     */
    window.progressReference =
        useRef(null);


    /*
     * Authentication state
     *
     * -1 = not checked
     *  0 = not auth
     *  1 = auth
     */
    const [
        isAuth,
        setIsAuth
    ] = useState(-1);


    /*
     * Save auth hook globally.
     *
     * Axios.js / sessionExpired() uses:
     *
     *     window.setIsAuth(0)
     *
     * When this happens App is rendered again and
     * ProtectedRoute receives the new authentication state.
     */
    window.setIsAuth =
        setIsAuth;


    /*
     * Important:
     *
     * This flag prevents multiple simultaneous 401 requests
     * from executing sessionExpired() several times.
     */
    window.session_expired =
        false;


    return (

        <HelmetProvider>

            {/*
             * ========================================================
             * META
             * ========================================================
             */}

            <Helmet>

                <title>
                    {t("meta.title")}
                </title>

                <meta
                    name="description"
                    content={
                        t(
                            "meta.description"
                        )
                    }
                />

            </Helmet>


            {/*
             * ========================================================
             * GLOBAL LOADING BAR
             * ========================================================
             */}

            <LoadingBar
                color={
                    window.progressBarColor
                }
                ref={
                    window.progressReference
                }
            />


            {/*
             * ========================================================
             * APPLICATION
             * ========================================================
             *
             * Do not render router until authentication has been
             * checked on initial page load.
             *
             * -1 = checking
             *  0 = not authenticated
             *  1 = authenticated
             */}

            {
                isAuth > -1 && (

                    <BrowserRouter>

                        <ScrollToTop />

                        <Previous />


                        <Routes>

                            {/*
                             * =================================================
                             * LOGIN
                             * =================================================
                             *
                             * ProtectedRoute receives isAuth explicitly.
                             *
                             * If:
                             *
                             *      isAuth = 0
                             *
                             * it renders:
                             *
                             *      <PageAuthentication path="login" />
                             *
                             * If:
                             *
                             *      isAuth = 1
                             *
                             * it redirects into the application.
                             */}

                            <Route
                                exact
                                path="/login"
                                element={
                                    <ProtectedRoute
                                        caller="login"
                                        isAuth={isAuth}
                                    />
                                }
                            />


                            {/*
                             * =================================================
                             * SIGNUP
                             * =================================================
                             */}

                            <Route
                                exact
                                path="/signup"
                                element={
                                    <ProtectedRoute
                                        caller="signup"
                                        isAuth={isAuth}
                                    />
                                }
                            />


                            {/*
                             * =================================================
                             * FORGOT PASSWORD
                             * =================================================
                             *
                             * IMPORTANT:
                             *
                             * PageAuthentication expects:
                             *
                             *      path="forgot"
                             *
                             * NOT:
                             *
                             *      element="forgot"
                             */}

                            <Route
                                path="/forgot"
                                element={
                                    <PageAuthentication
                                        path="forgot"
                                    />
                                }
                            />


                            <Route
                                path="/forgot/:uuid"
                                element={
                                    <PageAuthentication
                                        path="forgot"
                                    />
                                }
                            />


                            {/*
                             * =================================================
                             * LEGAL
                             * =================================================
                             */}

                            <Route
                                path="/terms"
                                element={
                                    <PageTerms />
                                }
                            />


                            <Route
                                path="/cookies"
                                element={
                                    <PageCookies />
                                }
                            />


                            {/*
                             * =================================================
                             * VALIDATE
                             * =================================================
                             *
                             * Kept as currently configured.
                             *
                             * If PageAuthentication needs to render a
                             * dedicated validation component in the future,
                             * "validate" will also need to exist inside
                             * PageAuthentication.Render().
                             */}

                            <Route
                                path="/validate/:uuid"
                                element={
                                    <PageAuthentication
                                        path="validate"
                                    />
                                }
                            />


                            {/*
                             * =================================================
                             * ROOT
                             * =================================================
                             *
                             * IMPORTANT:
                             *
                             * Previously this was:
                             *
                             *      <ProtectedRoute />
                             *
                             * So ProtectedRoute received:
                             *
                             *      isAuth = undefined
                             *      caller = undefined
                             *
                             * Now authentication state is explicitly passed.
                             */}

                            <Route
                                exact
                                path="/"
                                element={
                                    <ProtectedRoute
                                        caller="root"
                                        isAuth={isAuth}
                                    />
                                }
                            >

                                <Route
                                    path="/"
                                    element={
                                        <Navigate
                                            to="/dashboard"
                                            replace
                                        />
                                    }
                                />

                            </Route>


                            {/*
                             * =================================================
                             * DASHBOARD
                             * =================================================
                             *
                             * This is the key route for session expiration.
                             *
                             * When Axios receives HTTP 401:
                             *
                             *      sessionExpired()
                             *              ↓
                             *      window.setIsAuth(0)
                             *              ↓
                             *      App renders again
                             *              ↓
                             *      ProtectedRoute gets isAuth={0}
                             *              ↓
                             *      Navigate("/login")
                             */}

                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute
                                        caller="root"
                                        isAuth={isAuth}
                                    />
                                }
                            >

                                {/*
                                 * DASHBOARD MAIN
                                 */}

                                <Route
                                    path=""
                                    element={
                                        <PageDashboard
                                            path="dash"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * USER
                                 * =================================================
                                 */}

                                <Route
                                    path="user"
                                    element={
                                        <PageDashboard
                                            path="user"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * PLANTS BASE
                                 * =================================================
                                 */}

                                <Route
                                    path="plants"
                                    element={
                                        <PageDashboard
                                            path="plants"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * ADD PLANT
                                 * =================================================
                                 */}

                                <Route
                                    path="plants/add"
                                    element={
                                        <PageDashboard
                                            path="plants/add"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * DELETE MODE
                                 * =================================================
                                 *
                                 * There is intentionally NO:
                                 *
                                 *      /dashboard/plants/delete
                                 *
                                 * Delete mode is activated over the normal
                                 * plant lists using sessionStorage/location
                                 * state.
                                 */}


                                {/*
                                 * =================================================
                                 * SOLAR THERMAL
                                 * =================================================
                                 */}

                                <Route
                                    path="plants/thermal"
                                    element={
                                        <PageDashboard
                                            path="plants/thermal"
                                        />
                                    }
                                />


                                <Route
                                    path="plants/thermal/:id-:name"
                                    element={
                                        <PageDashboard
                                            path="plants/thermal/:id"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * PHOTOVOLTAIC
                                 * =================================================
                                 */}

                                <Route
                                    path="plants/photovoltaic"
                                    element={
                                        <PageDashboard
                                            path="plants/photovoltaic"
                                        />
                                    }
                                />


                                <Route
                                    path="plants/photovoltaic/:id-:name"
                                    element={
                                        <PageDashboard
                                            path="plants/photovoltaic/:id"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * TEMPERATURE
                                 * =================================================
                                 */}

                                <Route
                                    path="plants/temperature"
                                    element={
                                        <PageDashboard
                                            path="plants/temperature"
                                        />
                                    }
                                />


                                <Route
                                    path="plants/temperature/:id-:name"
                                    element={
                                        <PageDashboard
                                            path="plants/temperature/:id"
                                        />
                                    }
                                />


                                {/*
                                 * =================================================
                                 * WATER
                                 * =================================================
                                 */}

                                <Route
                                    path="plants/water"
                                    element={
                                        <PageDashboard
                                            path="plants/water"
                                        />
                                    }
                                />


                                <Route
                                    path="plants/water/:id-:name"
                                    element={
                                        <PageDashboard
                                            path="plants/water/:id"
                                        />
                                    }
                                />

                            </Route>


                            {/*
                             * =================================================
                             * NOT FOUND
                             * =================================================
                             */}

                            <Route
                                path="*"
                                element={
                                    <PageMessages
                                        element="404"
                                    />
                                }
                            />

                        </Routes>

                    </BrowserRouter>
                )
            }


            {/*
             * ========================================================
             * CHECK AUTH ON INITIAL LOAD
             * ========================================================
             */}

            {
                isAuth < 0 &&
                isAuthOnLoad()
            }


            {/*
             * ========================================================
             * DEVELOPMENT BANNER
             * ========================================================
             */}

            {
                process.env.NODE_ENV ===
                "development" && (

                    <div className="devel-banner">

                        Development

                    </div>
                )
            }

        </HelmetProvider>
    );
}